import { useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import { EmptyState, NotBuiltIcon, PageHeader, SectionCard, StatusChip } from '@platform/ui-kit';
import type { PortalModule } from '../api';

export interface ModulePlaceholderProps {
  modules: PortalModule[];
  /**
   * The module, when the caller already knows it.
   *
   * WITHOUT this the component looked the module up from `useParams().moduleId`, which
   * only exists on the generic `/:moduleId` routes. APR, AI Readiness and SPN each
   * enumerate their routes by depth (`/spn/:a/:b`) because React Router ranks a splat
   * below a dynamic segment — so their params are positional and `moduleId` is undefined.
   * The lookup failed and every not-yet-built screen in all three products reported
   * "Module not available … is not licensed for this install", which is a different and
   * alarming thing to tell someone about a screen that is merely pending.
   */
  module?: PortalModule;
  /** The section, likewise, when the caller knows it. */
  section?: { key: string; label: string };
}

/**
 * Landing screen for a module — or one of its sections — that is not built yet.
 *
 * Navigation is real ahead of the modules: the drawer drills in, the sections are
 * the ones the artifact specifies, and the breadcrumb trail is accurate. Only the
 * content is pending, and this says so rather than implying a broken install.
 */
export function ModulePlaceholder({
  modules,
  module: knownModule,
  section: knownSection,
}: ModulePlaceholderProps) {
  const { moduleId, sectionKey } = useParams<{ moduleId: string; sectionKey: string }>();
  const navigate = useNavigate();

  const module = knownModule ?? modules.find((m) => m.id === moduleId);

  if (!module) {
    return (
      <EmptyState
        title="Module not available"
        description={
          moduleId
            ? `"${moduleId}" is not licensed for this install, or has not been assigned to your account.`
            : 'Unknown module.'
        }
        action={
          <Button variant="contained" onClick={() => navigate('/')}>
            Back to start menu
          </Button>
        }
      />
    );
  }

  const section = knownSection ?? module.sections.find((s) => s.key === sectionKey);

  return (
    <>
      <PageHeader
        title={section ? section.label : module.name}
        description={section ? `${module.name} — ${section.label}` : module.description}
        meta={<StatusChip tone="neutral" label="Not built yet" />}
      />
      <SectionCard>
        <EmptyState
          icon={<NotBuiltIcon />}
          title={`${section ? section.label : module.name} is not built yet`}
          description="The platform foundation, navigation and Administration are in place. This module arrives in a later phase, built on the same shared components as the rest of the platform."
        />
      </SectionCard>
    </>
  );
}
